import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { emailValidator } from 'darwin_core/validators.js';

import { useAuth } from './AuthenticationContext.jsx';
import { isTransportError, TRANSPORT_ERROR_MESSAGE } from './transportError.js';
import BackLink from '../navigation/BackLink.jsx';
import { PASSWORD_CODE_PATH } from '../navigation/paths.js';
import HaloLoginDevices from '../components/HaloLoginDevices.jsx';
import LegalFooter from '../legal/LegalFooter.jsx';
import TextField from '../components/TextField.jsx';

// Neutral, always-identical confirmation. It never reveals whether the email has
// an account (avoids account enumeration), and it points the user at the reset
// email, whose link opens /password-code (ForgotPasswordCodeEntry) here or in the
// app.
const CONFIRMATION =
  "If that email address has a Halo account, we've sent a password-reset link. Open the link in the email to choose a new password. It can take a few minutes to arrive. If you can't see it, check your junk folder.";

// Forgot-password INITIATION. It triggers the Cognito reset email; the code-entry
// / new-password step is ForgotPasswordCodeEntry, at /password-code, which the
// email's link opens.
//
// Copy follows the app's ForgotPassword screen (app/src/screens/Auth/
// ForgotPassword.js): its heading, instruction, RESET PASSWORD button and
// "Already have a reset code?" link. Its header back-chevron becomes the BackLink
// at the top — the same affordance the signed-in web screens use — naming where
// it goes (Sign in). After sending, the app moves on to its code entry; the web
// says to open the email instead, because the email's link brings the code with
// it. `initialEmail` is the email typed on sign-in, if any.
export default function ForgotPassword({ onBackToSignIn, initialEmail = '' }) {
  const { forgotPassword } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState(initialEmail);
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const headingRef = useRef(null);

  // Move focus to the current heading when this view is shown (mounted from the
  // sign-in toggle) and when it switches to the confirmation, so screen readers
  // announce the change instead of silently swapping content.
  useEffect(() => {
    headingRef.current?.focus();
  }, [sent]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setPending(true);
    setError('');
    try {
      await forgotPassword(email);
      setSent(true);
    } catch (err) {
      if (isTransportError(err)) {
        // Never left the machine — don't claim a link was sent.
        setError(TRANSPORT_ERROR_MESSAGE);
      } else {
        // A Cognito-level response (including "no such account"): show the same
        // neutral confirmation either way, so it can't be used to enumerate emails.
        setSent(true);
      }
    } finally {
      setPending(false);
    }
  };

  // As in the app: check the email, then go to code entry with it.
  const handleAlreadyHaveCode = async () => {
    const address = email.trim();
    const validation = await emailValidator(address);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }
    navigate(PASSWORD_CODE_PATH, { state: { email: address } });
  };

  const nav = (
    <div className="auth-screen__nav">
      <BackLink onClick={onBackToSignIn} label="Sign in" />
    </div>
  );

  if (sent) {
    return (
      <main className="app-shell auth-screen">
        {nav}
        <div className="auth-brand">
          <HaloLoginDevices />
        </div>
        <h1
          id="main-content"
          className="auth-screen__heading"
          ref={headingRef}
          tabIndex={-1}
        >
          Check your email
        </h1>
        <p className="auth-screen__intro">{CONFIRMATION}</p>
        {/* For someone with the code but not the link, such as the reset
            email's own fallback steps ("Already have a reset code?") followed
            here. The address is checked, and can be corrected, on the next
            screen. */}
        <button
          type="button"
          className="link-button auth-form__link"
          onClick={() =>
            navigate(PASSWORD_CODE_PATH, { state: { email: email.trim() } })
          }
        >
          Already have a reset code?
        </button>
        <LegalFooter />
      </main>
    );
  }

  return (
    <main className="app-shell auth-screen">
      {nav}
      <div className="auth-brand">
        <HaloLoginDevices />
      </div>
      <h1
        id="main-content"
        className="auth-screen__heading"
        ref={headingRef}
        tabIndex={-1}
      >
        Forgot your password?
      </h1>
      <p className="auth-screen__intro">
        Please enter your verified email address to reset your password.
      </p>
      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <TextField
          id="forgot-email"
          name="email"
          type="email"
          label="Email"
          // The username, as on Sign in and the reset page, so password managers
          // offer the saved login's email; as in the app.
          autoComplete="username"
          className="text-field--tight"
          value={email}
          // An error is about the address as it was, so editing it clears the
          // error, and with it the field's invalid state.
          onChange={(event) => {
            setEmail(event.target.value);
            setError('');
          }}
          disabled={pending}
          required
          errorId={error ? 'forgot-error' : undefined}
        />
        {/* type="button" so Enter in the field still submits the form. */}
        <button
          type="button"
          className="link-button auth-form__link"
          onClick={handleAlreadyHaveCode}
          disabled={pending}
        >
          Already have a reset code?
        </button>
        {/* Always present so assistive tech has the live region before an error is
            set; only a transport failure or an invalid email for "Already have a
            reset code?" populates it. */}
        <p
          id="forgot-error"
          className="form-error"
          role="alert"
          aria-live="assertive"
        >
          {error}
        </p>
        <button type="submit" className="auth-form__submit" disabled={pending}>
          {pending ? 'Sending…' : 'Reset Password'}
        </button>
      </form>
      <LegalFooter />
    </main>
  );
}
